import { useCallback, useEffect } from 'react';
import {
  LexicalTypeaheadMenuPlugin,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, $nodesOfType, NodeKey, TextNode } from 'lexical';
import { $isCodeNode } from '@lexical/code';
import isEmpty from 'lodash/isEmpty';

import { ID } from '../../../../types';

import { useCurrentUser } from '../../../../auth/hooks/useAuth';
import { useMountEffect } from '../../../../common/hooks/useMountEffect';

import {
  $createMentionNode,
  $isMentionNode,
  CURRENT_USER_MENTION_NODE_CLASS_NAME,
  MentionNode
} from '../../utils/MentionNode';
import { MENTION_MARKDOWN_IMPORT_REGEXP } from '../../utils/mentionMarkdownTransformer';
import { MentionsMenuItem } from '../MentionsMenuItem';
import { PopoverWithPortal } from '../../../Popover';
import { MentionTypeaheadOption } from '../../utils/MentionTypeaheadOption';

import { isCurrentUserMentionId } from '../../utils/isCurrentUserMentionId';

import { mentionsPluginInitializeMentions } from './utils/mentionsPluginInitializeMentions';

import { PopoverPlacement } from '../../../Popover/popoverConstants';

const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
);

function checkForAtSignMentions(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }

  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];

    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2]
      };
    }
  }
  return null;
}

interface MentionsPluginProps {
  options: MentionTypeaheadOption[];
  initialMentionIds?: ID[];
  onQueryChange: (matchingString: string) => void;
}

function MentionsPlugin({
  options,
  initialMentionIds,
  onQueryChange
}: MentionsPluginProps) {
  const [editor] = useLexicalComposerContext();

  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id;
  const currentUserClient = currentUser?.client;

  // Highlights mentions of the current user (incl. pseudo mentions
  // like @All that they belong to) with a distinct color.
  // MentionNode.createDOM doesn't know the current user, so the class
  // is applied from outside via a mutation listener
  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    const highlightNode = (nodeKey: NodeKey) => {
      const node = $getNodeByKey(nodeKey);

      if (
        !$isMentionNode(node) ||
        !isCurrentUserMentionId(`${node.__id}`, {
          id: currentUserId,
          client: currentUserClient
        })
      ) {
        return;
      }

      const element = editor.getElementByKey(nodeKey);

      if (element) {
        element.className = CURRENT_USER_MENTION_NODE_CLASS_NAME;
      }
    };

    // Mentions already present at registration time (e.g. initial
    // editor state built from markdown) don't trigger mutations
    editor.getEditorState().read(() => {
      $nodesOfType(MentionNode).forEach((node) => highlightNode(node.getKey()));
    });

    return editor.registerMutationListener(MentionNode, (mutatedNodes) => {
      editor.getEditorState().read(() => {
        mutatedNodes.forEach((mutation, nodeKey) => {
          if (mutation !== 'destroyed') {
            highlightNode(nodeKey);
          }
        });
      });
    });
  }, [currentUserClient, currentUserId, editor]);

  // Converts mention markers ([@John Smith](#mention-123)) in plain text
  // (e.g. pasted from another message) into mention nodes
  useEffect(
    () =>
      editor.registerNodeTransform(TextNode, (textNode) => {
        if (!textNode.isSimpleText() || textNode.hasFormat('code')) {
          return;
        }

        if ($isCodeNode(textNode.getParent())) {
          return;
        }

        const match = textNode
          .getTextContent()
          .match(MENTION_MARKDOWN_IMPORT_REGEXP);

        if (!match) {
          return;
        }

        const startIndex = match.index || 0;
        const endIndex = startIndex + match[0].length;

        let targetNode: TextNode;
        if (startIndex === 0) {
          [targetNode] = textNode.splitText(endIndex);
        } else {
          [, targetNode] = textNode.splitText(startIndex, endIndex);
        }

        const [, mentionName, mentionId] = match;
        targetNode.replace($createMentionNode(mentionId, mentionName));
      }),
    [editor]
  );

  useMountEffect(() => {
    if (isEmpty(initialMentionIds)) {
      return;
    }

    mentionsPluginInitializeMentions({
      editor,
      options,
      initialMentionIds
    });
  });

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0
  });

  const onSelectOption = useCallback<
    (
      option: MentionTypeaheadOption,
      textNodeContainingQuery: TextNode,
      closeMenu: () => void
    ) => void
  >(
    (selectedOption, nodeToReplace, closeMenu) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(
          selectedOption.id,
          `@${selectedOption.name}`
        );

        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }

        mentionNode.select();
        closeMenu();
      });
    },
    [editor]
  );

  const checkForMentionMatch = useCallback<(text: string) => MenuTextMatch>(
    (text) => {
      const mentionMatch = checkForAtSignMentions(text, 0);

      const slashMatch = checkForSlashTriggerMatch(text, editor);

      return !slashMatch && mentionMatch ? mentionMatch : null;
    },
    [checkForSlashTriggerMatch, editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      anchorClassName="reset-menu-anchor"
      onQueryChange={onQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) =>
        anchorElementRef && options?.length ? (
          <PopoverWithPortal
            className="min-w-48 z-20 overflow-y-auto bg-white border border-transparent dark:bg-gray-700 dark:border-opacity-10 dark:border-white dark:text-white focus:outline-none py-1 ring-1 ring-black ring-opacity-5 rounded-md shadow-lg text-gray-700 max-h-60"
            placement={PopoverPlacement.TOP_START}
            referenceElement={anchorElementRef.current}
          >
            <ul>
              {options.map((option, i: number) => (
                <MentionsMenuItem
                  index={i}
                  isSelected={selectedIndex === i}
                  onClick={() => {
                    setHighlightedIndex(i);
                    selectOptionAndCleanUp(option);
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(i);
                  }}
                  key={option.key}
                  option={option}
                />
              ))}
            </ul>
          </PopoverWithPortal>
        ) : null
      }
    />
  );
}

export default MentionsPlugin;
